// Compatibility shim for host pages embedded before the Solopoint → Aevo rename.
//
// The public embed contract used to be `window.SolopointEchoConfig` (read) and
// `window.SolopointEcho` (exposed), and per-visitor state was stored under
// `solopoint_*` localStorage keys. Sites that still ship the old snippet keep
// working: the old config global is read as a fallback, the old API global is
// aliased to the new one, and old storage keys are moved to their new names
// once. Remove this file after every embedding site has switched to
// `window.AevoEchoConfig`.
import type { AevoEchoConfig } from './config'

export const LEGACY_STORAGE_PREFIX = 'solopoint_'
export const STORAGE_PREFIX = 'aevo_'

declare global {
  interface Window {
    /** @deprecated Use `window.AevoEchoConfig`. */
    SolopointEchoConfig?: AevoEchoConfig
    /** @deprecated Use `window.AevoEcho`. */
    SolopointEcho?: Window['AevoEcho']
  }
}

/** The host page's config: the current global, or the legacy one. */
export function readHostConfig(): AevoEchoConfig | undefined {
  if (typeof window === 'undefined') return undefined
  return window.AevoEchoConfig ?? window.SolopointEchoConfig
}

/** Expose the API object under the legacy global name too. */
export function aliasLegacyGlobal(): void {
  if (typeof window === 'undefined') return
  if (window.AevoEcho && !window.SolopointEcho) window.SolopointEcho = window.AevoEcho
}

/**
 * One-time move of `solopoint_*` localStorage entries to `aevo_*`. Keeps a
 * visitor's open conversation (and draft, launcher position, panel size)
 * across the rename. Never overwrites a value already stored under the new key.
 */
export function migrateLegacyStorage(): void {
  try {
    const moves: Array<[string, string]> = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(LEGACY_STORAGE_PREFIX)) {
        moves.push([key, STORAGE_PREFIX + key.slice(LEGACY_STORAGE_PREFIX.length)])
      }
    }
    for (const [from, to] of moves) {
      const value = localStorage.getItem(from)
      if (value !== null && localStorage.getItem(to) === null) localStorage.setItem(to, value)
      localStorage.removeItem(from)
    }
  } catch {
    // storage unavailable (private mode, blocked) — nothing to migrate
  }
}
